import { putBlob, putDataUrl } from './blobStore';

/**
 * Compress an image file and persist it to the blob store.
 *
 * Returns a `blob:sha256-<hex>` reference string which is stored in Dexie rows
 * instead of the raw bytes. Callers pass this string to `<BlobImage src={…}>`
 * for rendering.
 *
 * The compression pipeline: FileReader → <img> → canvas resize to fit
 * `maxWidth × maxHeight` → `canvas.toBlob('image/jpeg', quality)` → blob store.
 * Using `toBlob()` instead of `toDataURL()` saves the base64 round-trip, so
 * photos land on disk as raw bytes.
 */
export async function compressImage(
  file: File,
  options: { maxWidth?: number; maxHeight?: number; quality?: number } = {}
): Promise<string> {
  const { maxWidth = 1600, maxHeight = 1600, quality = 0.82 } = options;

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });

  let { width, height } = img;
  if (width > maxWidth || height > maxHeight) {
    const ratio = Math.min(maxWidth / width, maxHeight / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  // If we can't even get a 2D context, persist the original data URL so the
  // user doesn't lose their upload. It'll go through the normal blob store
  // path just like a legacy migration.
  if (!ctx) return await putDataUrl(dataUrl);
  ctx.drawImage(img, 0, 0, width, height);

  const mime = file.type === 'image/png' && quality === 1 ? 'image/png' : 'image/jpeg';

  const blob: Blob | null = await new Promise(resolve => {
    canvas.toBlob(resolve, mime, quality);
  });
  if (!blob) return await putDataUrl(canvas.toDataURL(mime, quality));
  return await putBlob(blob);
}
